/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/auth/AuthContext";
import { useStore } from "../../context/store/StoreContext";
import { loadLegalDocument } from "../../utils/legalDocuments";
import {
  clearOnboardingSession,
  clearSignupDraft,
  getOnboardingResumePath,
  getRegisterErrorMessage,
  isAccountExistsError,
  isAuthSubscriptionExpired,
  isStoreSlugTakenError,
  loadSignupDraft,
  loginExistingTenant,
  persistTenantAuth,
  saveSignupDraft,
} from "../utils/onboardingSession";
import { completeStoreOnboarding, TRIAL_WELCOME_KEY } from "../utils/completeStoreOnboarding";
import { submitUserLogin } from "../utils/submitUserLogin";
import { replaceAndLock } from "../../navigation/limitedBackHistory";
import { sanitizeStoreSlug } from "../../utils/sanitizeStoreSlug";
import AuthUI from "./AuthUI";
import {
  AUTH_COPY,
  cleanStoreHandle,
  digitsOnlyPhone,
  formatIraqiPhone,
  isValidIraqiPhone,
  isValidStoreHandle,
  passwordMeetsAllRules,
} from "./authCopy";

const MAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AuthPage({ initialMode = "signup" }) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, isAuthenticated, authRole, isLoading } = useAuth();
  const { fetchStoreData } = useStore();
  const t = AUTH_COPY[isArabic ? "ar" : "en"];

  const pathMode = location.pathname.includes("/login") ? "login" : "signup";
  const [mode, setModeState] = useState(initialMode === "login" || pathMode === "login" ? "login" : "signup");
  const [loginIdMode, setLoginIdMode] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [showPassLi, setShowPassLi] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState("privacy");
  const [legalContent, setLegalContent] = useState("");
  const [legalLoading, setLegalLoading] = useState(false);

  const draft = useMemo(() => loadSignupDraft() || {}, []);
  const [signup, setSignup] = useState({
    fullName: draft.fullName || "",
    storeName: draft.storeName || "",
    phone: draft.phone ? formatIraqiPhone(draft.phone) : "",
    email: draft.email || "",
    storeSlug: draft.storeSlug || "",
    password: "",
    confirmPassword: "",
  });
  const [login, setLogin] = useState({ phone: "", email: "", password: "" });

  const handleValid = isValidStoreHandle(signup.storeSlug);

  useEffect(() => {
    document.title = t.docTitle;
  }, [t.docTitle]);

  useEffect(() => {
    const next = location.pathname.includes("/login") ? "login" : "signup";
    setModeState(next);
  }, [location.pathname]);

  useEffect(() => {
    if (isLoading || loading) return;
    if (isAuthenticated()) {
      if (authRole === "superAdmin") replaceAndLock(navigate, "/admin");
      else if (authRole === "tenant") replaceAndLock(navigate, getOnboardingResumePath());
      else replaceAndLock(navigate, "/");
    }
  }, [isLoading, loading, isAuthenticated, authRole, navigate]);

  const setMode = (next) => {
    setModeState(next);
    setFormError("");
    setErrors({});
    navigate(next === "login" ? "/login" : "/signup", { replace: true });
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormError("");

    if (name === "phone" || name === "loginPhone") {
      const formatted = formatIraqiPhone(value);
      if (name === "phone") {
        setSignup((prev) => {
          const next = { ...prev, phone: formatted };
          saveSignupDraft({ ...next, phone: digitsOnlyPhone(formatted) });
          return next;
        });
        clearError("phone");
      } else {
        setLogin((prev) => ({ ...prev, phone: formatted }));
        clearError("loginPhone");
      }
      return;
    }

    if (name === "storeSlug") {
      const cleaned = cleanStoreHandle(value);
      setSignup((prev) => {
        const next = { ...prev, storeSlug: cleaned };
        saveSignupDraft({ ...next, phone: digitsOnlyPhone(next.phone) });
        return next;
      });
      clearError("storeSlug");
      return;
    }

    if (name === "loginEmail") {
      setLogin((prev) => ({ ...prev, email: value }));
      clearError("loginEmail");
      return;
    }

    if (name === "loginPassword") {
      setLogin((prev) => ({ ...prev, password: value }));
      clearError("loginPassword");
      return;
    }

    setSignup((prev) => {
      const next = { ...prev, [name]: value };
      saveSignupDraft({ ...next, phone: digitsOnlyPhone(next.phone) });
      return next;
    });
    clearError(name);
  };

  const toggleLanguage = () => {
    const next = isArabic ? "en" : "ar";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
    document.documentElement.lang = next;
    document.documentElement.dir = next === "ar" ? "rtl" : "ltr";
    document.body.dir = next === "ar" ? "rtl" : "ltr";
  };

  const openLegal = async (docKey) => {
    setLegalDoc(docKey);
    setLegalOpen(true);
    setLegalContent("");
    setLegalLoading(true);
    try {
      const text = await loadLegalDocument(docKey, isArabic ? "ar" : "en");
      setLegalContent(text);
    } catch {
      setLegalContent(isArabic ? "تعذر تحميل المستند حالياً." : "Unable to load the document right now.");
    } finally {
      setLegalLoading(false);
    }
  };

  const notify = (type, msg) =>
    toast[type](msg, {
      position: "top-center",
      rtl: isArabic,
      theme: "colored",
      autoClose: 2800,
    });

  const notifySubscription = (data, fallback, { expired } = {}) => {
    const note = data?.subscription?.notification;
    if (note) {
      notify(expired ? "warning" : "info", note);
      return;
    }
    if (fallback) notify("success", fallback);
  };

  const finishOnboardingAndGo = async ({ storeName, storeSlug }) => {
    await completeStoreOnboarding({ storeName, storeSlug, isArabic });
    sessionStorage.removeItem(TRIAL_WELCOME_KEY);
    clearSignupDraft();
    clearOnboardingSession();
    const tenantId = localStorage.getItem("tenantId");
    if (tenantId && typeof fetchStoreData === "function") {
      try {
        await fetchStoreData(tenantId);
      } catch {
        /* dashboard can load store data later */
      }
    }
    replaceAndLock(navigate, "/admin-store");
  };

  const validateSignup = () => {
    const next = {};
    if (signup.fullName.trim().length < 2) next.fullName = true;
    if (signup.storeName.trim().length < 3) next.storeName = true;
    if (!isValidIraqiPhone(signup.phone)) next.phone = true;
    if (!MAIL_RE.test(signup.email.trim())) next.email = true;
    if (!isValidStoreHandle(signup.storeSlug)) next.storeSlug = true;
    if (!passwordMeetsAllRules(signup.password)) next.password = true;
    if (!signup.confirmPassword || signup.confirmPassword !== signup.password) {
      next.confirmPassword = true;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (!validateSignup()) {
      setFormError(t.formFix);
      return;
    }

    setLoading(true);
    setFormError("");
    const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(
      /\/+$/,
      ""
    );
    const phone = digitsOnlyPhone(signup.phone);
    const storeSlug = sanitizeStoreSlug(signup.storeSlug, { finalize: true });
    const body = {
      tenantName: signup.fullName.trim(),
      engTenantName: signup.fullName.trim(),
      phone,
      email: signup.email.trim(),
      password: signup.password,
      confirmPassword: signup.confirmPassword,
      storeSlug,
    };

    try {
      const res = await fetch(`${baseUrl}/Register/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      const contentType = res.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await res.json() : await res.text();
      const registerFailed = !res.ok || (typeof data === "object" && data?.success === false);

      if (registerFailed) {
        const message = getRegisterErrorMessage(data, `HTTP ${res.status}`);

        if (isStoreSlugTakenError(message, data)) {
          setErrors((prev) => ({ ...prev, storeSlug: message }));
          setFormError(message);
          return;
        }

        if (isAccountExistsError(message)) {
          try {
            const loginData = await loginExistingTenant(signup.email.trim(), signup.password, baseUrl);
            const persistedLogin = persistTenantAuth(loginData, authLogin, {
              source: "login",
              identifier: signup.email.trim(),
            });
            if (!persistedLogin) {
              setFormError(t.accountExists);
              return;
            }
            const expired = isAuthSubscriptionExpired(loginData);
            const details = loginData?.tenantDetails || {};
            const hasStore = Boolean(
              details.clientName ||
                details.ClientName ||
                localStorage.getItem("companyStoreName")
            );
            const hasSlug = Boolean(
              details.storeSlug ||
                details.StoreSlug ||
                localStorage.getItem("companyStoreSlug")
            );
            notifySubscription(loginData, t.loggedIn, { expired });
            if (hasStore && hasSlug) {
              clearSignupDraft();
              replaceAndLock(navigate, "/admin-store");
              return;
            }
            if (expired) {
              clearSignupDraft();
              replaceAndLock(navigate, "/theme-plan");
              return;
            }
            await finishOnboardingAndGo({
              storeName: signup.storeName.trim(),
              storeSlug,
            });
            return;
          } catch {
            setFormError(t.accountExists);
            return;
          }
        }

        setFormError(message);
        return;
      }

      const registerData = typeof data === "object" ? data : {};
      const persisted = persistTenantAuth(registerData, authLogin, {
        source: "register",
        identifier: signup.email.trim(),
      });
      if (!persisted) {
        setFormError(t.networkErr);
        return;
      }
      notifySubscription(registerData, t.created, {
        expired: isAuthSubscriptionExpired(registerData),
      });
      await finishOnboardingAndGo({
        storeName: signup.storeName.trim(),
        storeSlug,
      });
    } catch (err) {
      if (err?.code === "SLUG_TAKEN" || err?.message === "SLUG_TAKEN") {
        setErrors((prev) => ({ ...prev, storeSlug: t.slugTaken }));
        setFormError(t.slugTaken);
      } else {
        setFormError(err?.message || t.networkErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (loginIdMode === "phone") {
      if (!isValidIraqiPhone(login.phone)) next.loginPhone = true;
    } else if (!MAIL_RE.test(login.email.trim())) {
      next.loginEmail = true;
    }
    if (!login.password) next.loginPassword = true;
    setErrors(next);
    if (Object.keys(next).length) {
      setFormError(t.formFix);
      return;
    }

    setLoading(true);
    setFormError("");
    const identifier =
      loginIdMode === "phone" ? digitsOnlyPhone(login.phone) : login.email.trim();

    try {
      const result = await submitUserLogin({
        identifier,
        password: login.password,
        isArabic,
        authLogin,
        fetchStoreData,
      });
      notifySubscription(
        { subscription: result.subscription },
        t.loggedIn,
        { expired: result.tenantPlanExpired }
      );
      replaceAndLock(navigate, result.nextPath || getOnboardingResumePath());
    } catch (error) {
      let errorMessage = error.message;
      if (errorMessage?.includes("fetch")) errorMessage = t.networkErr;
      setFormError(errorMessage || (isArabic ? "فشل تسجيل الدخول" : "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-center" rtl={isArabic} />
      <AuthUI
        isArabic={isArabic}
        mode={mode}
        setMode={setMode}
        loginIdMode={loginIdMode}
        setLoginIdMode={setLoginIdMode}
        signup={signup}
        login={login}
        errors={errors}
        clearError={clearError}
        handleChange={handleChange}
        handleSignupSubmit={handleSignupSubmit}
        handleLoginSubmit={handleLoginSubmit}
        loading={loading}
        formError={formError}
        showPass={showPass}
        setShowPass={setShowPass}
        showPass2={showPass2}
        setShowPass2={setShowPass2}
        showPassLi={showPassLi}
        setShowPassLi={setShowPassLi}
        handleValid={handleValid}
        legalOpen={legalOpen}
        legalDoc={legalDoc}
        legalContent={legalContent}
        legalLoading={legalLoading}
        openLegal={openLegal}
        closeLegal={() => setLegalOpen(false)}
        toggleLanguage={toggleLanguage}
      />
    </>
  );
}
